import { ref, computed, type Ref } from 'vue';
import { SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import { useInfiniteScrollObserver, useCustomToast } from '@/shared/lib';
import type { Pagination } from '@/shared/lib';

const SHOW_ALL_BATCH_SIZE = 30;
const DEFAULT_QUANTITY = 10;

export interface RecordFetchParams {
  page: number;
  itemsPerPage: number;
}

/**
 * `stale` means a newer request superseded this one, so the caller must not roll anything back:
 * the state it would restore is no longer the latest choice.
 */
export type RecordSearchOutcome = 'ok' | 'failed' | 'stale' | 'skipped';

export type RecordFetcher<T> = (
  params: RecordFetchParams
) => Promise<{ data: T[]; metadata: Pagination }>;

const DEFAULT_ERROR_TITLE = 'Erro ao carregar registros';

export function useRecordSearch<T extends { id: number }>(
  fetcher: Ref<RecordFetcher<T>>,
  errorTitle: () => string = () => DEFAULT_ERROR_TITLE
) {
  const { showErrorToast } = useCustomToast();

  const items = ref<T[]>([]) as Ref<T[]>;
  const allItems = ref<T[]>([]) as Ref<T[]>;
  const metadata = ref<Pagination>();
  const loading = ref(false);
  const loadingMore = ref(false);
  const hasMoreItems = ref(true);
  const selectedQuantity = ref(DEFAULT_QUANTITY);
  const currentPage = ref(1);

  const isShowAllMode = computed(() => selectedQuantity.value === SHOW_ALL_ITEMS_QUANTITY);

  let requestId = 0;
  let reloading = false;

  const getBatchSize = () => (isShowAllMode.value ? SHOW_ALL_BATCH_SIZE : selectedQuantity.value);

  const performSearch = async (
    page = 1,
    append = false,
    silent = false
  ): Promise<RecordSearchOutcome> => {
    if (append && (loadingMore.value || !hasMoreItems.value)) return 'skipped';

    const id = ++requestId;
    loadingMore.value = append;
    loading.value = !append && !silent;
    reloading = silent;

    try {
      const itemsPerPage = getBatchSize();
      const response = await fetcher.value({ page, itemsPerPage });
      if (id !== requestId) return 'stale';

      items.value = response.data;
      metadata.value = response.metadata;

      if (isShowAllMode.value) {
        allItems.value = append ? [...allItems.value, ...response.data] : [...response.data];
        const total = response.metadata?.total ?? 0;
        hasMoreItems.value = allItems.value.length < total;
      } else {
        allItems.value = [];
        hasMoreItems.value = response.data.length === itemsPerPage;
      }
      return 'ok';
    } catch (error) {
      if (id !== requestId) return 'stale';
      console.error('Erro ao buscar registros:', error);
      showErrorToast({
        title: errorTitle(),
        message: 'Tente novamente mais tarde.',
        type: 'error',
      });
      // The rows already on screen stay: an empty list here would read as "no results".
      return 'failed';
    } finally {
      if (id === requestId) {
        loading.value = false;
        loadingMore.value = false;
        reloading = false;
      }
    }
  };

  const snapshotPaging = () => ({
    currentPage: currentPage.value,
    hasMoreItems: hasMoreItems.value,
    selectedQuantity: selectedQuantity.value,
  });

  const restorePaging = (snapshot: ReturnType<typeof snapshotPaging>) => {
    currentPage.value = snapshot.currentPage;
    hasMoreItems.value = snapshot.hasMoreItems;
    selectedQuantity.value = snapshot.selectedQuantity;
  };

  /**
   * Paging state moves before the request so the controls answer at once, and moves back when the
   * request fails, so it keeps describing the rows that are still on screen.
   */
  const searchWithRollback = async (page: number, apply: () => void) => {
    const snapshot = snapshotPaging();
    apply();

    const outcome = await performSearch(page);
    if (outcome === 'failed') restorePaging(snapshot);

    return outcome;
  };

  const refetch = () =>
    searchWithRollback(1, () => {
      currentPage.value = 1;
      hasMoreItems.value = true;
    });

  const onPageChanged = (page: number) =>
    searchWithRollback(page, () => {
      currentPage.value = page;
    });

  const handleQuantityChange = (value: string | number) =>
    searchWithRollback(1, () => {
      selectedQuantity.value = Number(value);
      currentPage.value = 1;
      hasMoreItems.value = true;
    });

  const reload = async (): Promise<RecordSearchOutcome> => {
    const snapshot = snapshotPaging();
    if (isShowAllMode.value) {
      currentPage.value = 1;
      hasMoreItems.value = true;
    }

    let outcome = await performSearch(currentPage.value, false, true);
    const lastPage = Math.max(metadata.value?.totalPages ?? 1, 1);
    if (outcome === 'ok' && currentPage.value > lastPage) {
      currentPage.value = lastPage;
      outcome = await performSearch(lastPage, false, true);
    }

    if (outcome === 'failed') restorePaging(snapshot);
    return outcome;
  };

  const loadMore = async () => {
    // A running refetch replaces `allItems`; a batch appended meanwhile would be thrown away or
    // supersede it, since the rows of the previous query now stay visible while it loads.
    if (loading.value || loadingMore.value || reloading || !hasMoreItems.value) return;
    if (allItems.value.length < SHOW_ALL_BATCH_SIZE) return;
    currentPage.value += 1;

    const outcome = await performSearch(currentPage.value, true);
    if (outcome === 'failed') currentPage.value -= 1;
  };

  /**
   * The row the listing is showing for `id`. In "show all" mode the rendered array is `allItems`,
   * so the lookup follows the same array the screen reads.
   */
  const findItem = (id: number): T | undefined =>
    (isShowAllMode.value ? allItems.value : items.value).find((item) => item.id === id);

  /**
   * Rewrites one row where it stands, with no request. Both arrays are touched because the same
   * row lives in `items` and in `allItems` while the continuous scroll is on.
   */
  const patchItem = (id: number, patch: Partial<T>) => {
    const apply = (list: T[]) => list.map((item) => (item.id === id ? { ...item, ...patch } : item));

    items.value = apply(items.value);
    allItems.value = apply(allItems.value);
  };

  /**
   * Drops one row from the listing, and the total goes down with it: the summary and the page
   * buttons read `metadata`, so leaving it untouched would count a row that is no longer there.
   */
  const removeItem = (id: number) => {
    const reject = (list: T[]) => list.filter((item) => item.id !== id);
    const sizeBefore = items.value.length + allItems.value.length;

    items.value = reject(items.value);
    allItems.value = reject(allItems.value);

    if (sizeBefore === items.value.length + allItems.value.length) return;

    const current = metadata.value;
    if (!current) return;

    metadata.value = {
      ...current,
      total: Math.max(current.total - 1, 0),
      ...(current.totalItems === undefined
        ? {}
        : { totalItems: Math.max(current.totalItems - 1, 0) }),
    };
  };

  const clear = () => {
    items.value = [];
    allItems.value = [];
    metadata.value = undefined;
  };

  useInfiniteScrollObserver({
    enabled: isShowAllMode,
    loadingMore,
    hasMore: hasMoreItems,
    onLoadMore: loadMore,
  });

  return {
    clear,
    items,
    allItems,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    selectedQuantity,
    currentPage,
    isShowAllMode,
    refetch,
    reload,
    onPageChanged,
    handleQuantityChange,
    loadMore,
    findItem,
    patchItem,
    removeItem,
  };
}
